/**
 * SOLID Review
 * S — Strong. One concern: the welcome cups' animation as data — the keyframes the stage's elements play, the timeline they play on,
 *     and when the shake phase (which the sound lines up with) starts and the whole sequence ends.
 * O — Acceptable. A new animated element is a keyframe here, an animation string here, and an element in the view.
 * L — N/A — data.
 * I — Strong. The style sheet, the seven animations, and the two times the model reads.
 * D — Strong. Imports nothing.
 */

// ── The keyframes (frontend-framework-oo P3, 2026-10-29, §28.295) ─────────────────────────────────────────────────────
// `WelcomeScreen.tsx` injected them into `document.head` when it was imported; its model adds them when the screen first shows,
// through the platform's style sheets (views touch no globals).
//
// Geometry the deltas below are derived from (see the stage constants near
// WelcomeScreen): stage 210×210, cups 79.2×105.6 (1.1× artwork) at (0,104.4)
// and (130.8,104.4), dice 17.6px starting between the cups at y=190.4. The
// dice deltas (-63|-67,-98) rest them side by side on the flipped left cup's
// upturned base (dome top y≈109.4). The right cup hovers mouth-down directly
// above the stack (70% keyframe), then slides straight DOWN onto the lower
// cup: the -29px landing delta nests its mouth ~55px over the lower cup's
// tapering body — the mouth (~55px inner) meets the body where it widens to
// match — swallowing the dice inside its cavity.
export const WELCOME_KEYFRAMES_ID = 'welcome-anim-styles';
export const WELCOME_KEYFRAMES = `
    @keyframes wsFlipLeft {
      0%   { transform: translateY(0) rotate(0deg); }
      45%  { transform: translateY(-48px) rotate(-100deg); }
      100% { transform: translateY(0) rotate(-180deg); }
    }
    @keyframes wsCupSlide {
      0%   { transform: translate(0, 0) rotate(0deg); }
      40%  { transform: translate(-72px, -170px) rotate(-95deg); }
      70%  { transform: translate(-131px, -100px) rotate(-180deg); }
      100% { transform: translate(-131px, -29px) rotate(-180deg); }
    }
    @keyframes wsDieArcA {
      0%   { transform: translate(0, 0); }
      45%  { transform: translate(-38px, -160px); }
      80%  { transform: translate(-63px, -98px); }
      90%  { transform: translate(-63px, -108px); }
      100% { transform: translate(-63px, -98px); }
    }
    @keyframes wsDieArcB {
      0%   { transform: translate(0, 0); }
      45%  { transform: translate(-34px, -172px); }
      82%  { transform: translate(-67px, -98px); }
      92%  { transform: translate(-67px, -105px); }
      100% { transform: translate(-67px, -98px); }
    }
    @keyframes wsDieTumbleA {
      0%   { transform: rotateX(-22deg) rotateY(-32deg); }
      100% { transform: rotateX(698deg) rotateY(-392deg); }
    }
    @keyframes wsDieTumbleB {
      0%   { transform: rotateX(-22deg) rotateY(-32deg); }
      100% { transform: rotateX(-742deg) rotateY(688deg); }
    }
    @keyframes wsStackShake {
      0%   { transform: translateX(0)    rotate(0deg)  translateY(0); }
      10%  { transform: translateX(8px)  rotate(30deg) translateY(0); }
      20%  { transform: translateX(16px) rotate(30deg) translateY(-26px); }
      30%  { transform: translateX(24px) rotate(30deg) translateY(2px); }
      40%  { transform: translateX(32px) rotate(30deg) translateY(-26px); }
      50%  { transform: translateX(40px) rotate(30deg) translateY(2px); }
      60%  { transform: translateX(48px) rotate(30deg) translateY(-26px); }
      70%  { transform: translateX(56px) rotate(30deg) translateY(2px); }
      80%  { transform: translateX(62px) rotate(30deg) translateY(-16px); }
      90%  { transform: translateX(65px) rotate(10deg) translateY(0); }
      100% { transform: translateX(65px) rotate(0deg)  translateY(0); }
    }
  `;

// ── The timeline ──────────────────────────────────────────────────────────────────────────────────────────────────────
//
// Animation timeline (seconds from trigger):
//   0.0–0.6  idle beat
//   0.6–1.5  left cup hop-flips 180° in place (mouth now facing down)
//   0.7–1.7  the two dice arc up and land on the flipped cup's upturned base,
//            tumbling in 3D, showing 2 and 1 — "Meyer!"
//   2.0–3.2  right cup arcs over, flipping 180°, hovers mouth-down above the
//            left cup, then slides straight down onto it, nesting over its
//            body and swallowing the dice
//   3.6–5.8  the combined stack tilts to a held 30° from vertical and pumps
//            up and down along its own (tilted) axis — the translateY in the
//            wsStackShake keyframes comes after the rotate, so it moves in the
//            rotated frame — walking to the stage centre, then settles upright
// End state: both cups upside-down, stacked, dice trapped inside (all
// animations fill forwards). Tapping the stage replays the whole sequence.

// Timeline (s)
const FLIP_DELAY_S = 0.6;
const FLIP_DUR_S = 0.9;
const DIE_DELAY_S = 0.7;
const DIE_DUR_S = 1.0;
const SLIDE_DELAY_S = 2.0;
const SLIDE_DUR_S = 1.2;
export const SHAKE_DELAY_S = 3.6;
const SHAKE_DUR_S = 2.2;
export const SEQUENCE_MS = (SHAKE_DELAY_S + SHAKE_DUR_S) * 1000 + 100;

// Linear so the pump cycles encoded in the keyframes play evenly.
const SHAKE_EASE = 'linear';
export const ANIM_FLIP     = `wsFlipLeft ${FLIP_DUR_S}s ease-in-out ${FLIP_DELAY_S}s forwards`;
export const ANIM_SLIDE    = `wsCupSlide ${SLIDE_DUR_S}s ease-in-out ${SLIDE_DELAY_S}s forwards`;
export const ANIM_ARC_A    = `wsDieArcA ${DIE_DUR_S}s linear ${DIE_DELAY_S}s forwards`;
export const ANIM_ARC_B    = `wsDieArcB ${DIE_DUR_S}s linear ${DIE_DELAY_S}s forwards`;
export const ANIM_TUMBLE_A = `wsDieTumbleA ${DIE_DUR_S}s ease-out ${DIE_DELAY_S}s forwards`;
export const ANIM_TUMBLE_B = `wsDieTumbleB ${DIE_DUR_S}s ease-out ${DIE_DELAY_S}s forwards`;
export const ANIM_SHAKE    = `wsStackShake ${SHAKE_DUR_S}s ${SHAKE_EASE} ${SHAKE_DELAY_S}s forwards`;
